import { Injectable } from '@angular/core';
import { Settings } from '../../core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AccessionData, VisitDetailsRequest, CollectionStatusData, UpdateContainerRequestInfo, AlternateContainerReasonData } from './register-sample-info';
import { AdditionalContainerInfo } from './additional-container/additional-container.component';
import * as moment from 'moment';

@Injectable()
export class RegisterSampleStorage {

    public sampleManagementUrl = `${this.settings.apiUrl}/investigator/sampleregistration`;

    constructor(
        private settings: Settings,
        private http: HttpClient) {
    }

    public getAccessionDetails(accessionNumber: string): Observable<any> {
        const localDate = new Date();
        const userLocale = navigator.language || 'en-US';
        return this.http.get(`${this.sampleManagementUrl}/${accessionNumber}?date= ${localDate.toLocaleString()}&culture=${userLocale}`);
    }

    public getVisitDetails(accessionData: AccessionData, isOverrideVisit = false): Observable<any> {
        let requestData: VisitDetailsRequest = {
            kitType: accessionData.kitType,
            kitTypeId: accessionData.kitTypeId,
            subprotocolId: accessionData.subProtocolId,
            overrideVisit: isOverrideVisit
        };
        return this.http.post(`${this.sampleManagementUrl}/visitdetails`, requestData).pipe(map((result: any) => {
            return result.sampleRegistrationVisitData;
        }));
    }

    public saveVisitDetails(details: any): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/savevisitdetails`, details);
    }

    public getRegisteredVisitDetails(accession: string): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/registeredvisitdetails`, { accessionNumber: accession }).pipe(map((result: any) => {
            return result.sampleRegistrationRegisteredVisitData;
        }));
    }

    public getContainerList(accession: string): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/listofcontainers`, { accessionNumber: accession }).pipe(map((result: any) => {
            return result.sampleRegistrationContainerListData;
        }));
    }

    public getCollectionStatusList(): Observable<any> {
        return this.http.get(`${this.sampleManagementUrl}/samplecollectionstatus`).pipe(map((result: any) => {
            if (result.sampleRegistrationCollectionStatusData) {
                let data: CollectionStatusData[] = result.sampleRegistrationCollectionStatusData;
                return data.map(status => {
                    return {
                        key: status.collectionStatusId, value: status.statusDescription, showReason: !status.isCollected
                    };
                });
            }
            return [];
        }));
    }

    public getReasonsList(): Observable<any[]> {
        return this.http.get(`${this.sampleManagementUrl}/samplenotcollectedstatus`).pipe(map((result: any) => {
            if (result.sampleRegistrationSampleNotCollectionStatusData) {
                let data: CollectionStatusData[] = result.sampleRegistrationSampleNotCollectionStatusData;
                return data.map(status => {
                    return {
                        key: status.notCollectedReasonId, value: status.notCollectedReasonDescription, showOther: status.isCommentRequired
                    };
                });
            }
            return [];
        }));
    }

    public deleteSampleRegistration(request: any) {
        return this.http.post(`${this.sampleManagementUrl}/deletesampleregistration`, request);
    }

    public submitSampleRegistration(request: any): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/submitregistration`, request);
    }

    public updateContainer(requestInfo: UpdateContainerRequestInfo): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/updateregisteredcontainer`, requestInfo).pipe(map((result: any) => result));
    }

    public insertAdditionalContainer(info: AdditionalContainerInfo, id: number) {
        let request = {
            registeredVisitId: id,
            barcode: info.barcode,
            containerDescription: info.reason,
            createdOn: `${moment().local().format('YYYY-MM-DD hh:mm:ss Z')}`
        };
        return this.http.post(`${this.sampleManagementUrl}/insertadditionalcontainer`, request).pipe(map((result: any) => result));
    }

    public getAlternateSampleReason(): Observable<any> {
        return this.http.get(`${this.sampleManagementUrl}/alternateSampleReason`).pipe(map((result: any) => {
            if (result.sampleRegistrationAlternateSampleReasonData) {
                let data: AlternateContainerReasonData[] = result.sampleRegistrationAlternateSampleReasonData;
                return data.map(status => {
                    return {
                        key: status.alternateReasonId, value: status.alternateReasonDescription
                    };
                });
            }
            return [];
        }));
    }

    public resetAlternateContainer(requestInfo: any): Observable<any> {
        return this.http.post(`${this.sampleManagementUrl}/removeregisteredalternatecontainer`, requestInfo).pipe(map((result: any) => result));

    }
}